/**
 * Currency Utilities
 *
 * Provides precise decimal arithmetic for currency calculations using Decimal.js.
 * Avoids JavaScript floating-point errors (e.g., 0.1 + 0.2 !== 0.3).
 */

import Decimal from 'decimal.js';

// Configure Decimal.js for currency calculations
Decimal.set({
  precision: 20, // High precision for intermediate calculations
  rounding: Decimal.ROUND_HALF_UP, // Standard currency rounding
  toExpNeg: -9, // Avoid exponential notation for small numbers
  toExpPos: 21, // Avoid exponential notation for large numbers
});

/**
 * CurrencyUtil provides static methods for precise currency calculations.
 *
 * All arithmetic methods return strings to preserve precision.
 * Use format() for locale-aware display formatting.
 *
 * @example
 * ```typescript
 * // Arithmetic operations
 * const sum = CurrencyUtil.add('0.1', '0.2'); // '0.30'
 * const diff = CurrencyUtil.subtract('10.00', '3.33'); // '6.67'
 * const product = CurrencyUtil.multiply('100.00', '0.15'); // '15.00'
 * const quotient = CurrencyUtil.divide('100.00', '3'); // '33.33'
 *
 * // Sum multiple values
 * const total = CurrencyUtil.sum(['10.50', '20.25', '5.75']); // '36.50'
 *
 * // Format for display
 * const formatted = CurrencyUtil.format(1234.56, 'BRL', 'pt-BR'); // 'R$ 1.234,56'
 * ```
 */
export class CurrencyUtil {
  /**
   * Adds two currency values.
   *
   * @param a - First value (string or number)
   * @param b - Second value (string or number)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Result as string with specified decimal places
   */
  static add(a: string | number, b: string | number, decimals = 2): string {
    const result = new Decimal(a.toString()).plus(new Decimal(b.toString()));
    return result.toFixed(decimals);
  }

  /**
   * Subtracts two currency values.
   *
   * @param a - Value to subtract from (string or number)
   * @param b - Value to subtract (string or number)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Result as string with specified decimal places
   */
  static subtract(a: string | number, b: string | number, decimals = 2): string {
    const result = new Decimal(a.toString()).minus(new Decimal(b.toString()));
    return result.toFixed(decimals);
  }

  /**
   * Multiplies two values.
   *
   * @param a - First value (string or number)
   * @param b - Second value (string or number)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Result as string with specified decimal places
   */
  static multiply(a: string | number, b: string | number, decimals = 2): string {
    const result = new Decimal(a.toString()).times(new Decimal(b.toString()));
    return result.toFixed(decimals);
  }

  /**
   * Divides two values.
   *
   * @param a - Dividend (string or number)
   * @param b - Divisor (string or number)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Result as string with specified decimal places
   * @throws Error if divisor is zero
   */
  static divide(a: string | number, b: string | number, decimals = 2): string {
    const divisor = new Decimal(b.toString());
    if (divisor.isZero()) {
      throw new Error('Division by zero');
    }
    const result = new Decimal(a.toString()).dividedBy(divisor);
    return result.toFixed(decimals);
  }

  /**
   * Sums an array of currency values.
   *
   * @param values - Array of values to sum (strings or numbers)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Total as string with specified decimal places
   */
  static sum(values: (string | number)[], decimals = 2): string {
    const total = values.reduce(
      (acc, val) => acc.plus(new Decimal(val.toString())),
      new Decimal(0),
    );
    return total.toFixed(decimals);
  }

  /**
   * Converts a number to a decimal string with rounding.
   *
   * @param value - Value to convert (string or number)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Formatted string with specified decimal places
   */
  static toDecimalString(value: string | number, decimals = 2): string {
    return new Decimal(value.toString()).toFixed(decimals);
  }

  /**
   * Formats a currency value for display using Intl.NumberFormat.
   *
   * @param value - Value to format (number or string)
   * @param currencyCode - ISO 4217 currency code (e.g., 'EUR', 'BRL', 'USD')
   * @param locale - BCP 47 locale string (e.g., 'pt-BR', 'en-US')
   * @returns Locale-formatted currency string
   */
  static format(value: number | string, currencyCode: string, locale: string): string {
    const numericValue =
      typeof value === 'string' ? parseFloat(new Decimal(value).toFixed(2)) : value;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  }

  /**
   * Compares two currency values.
   *
   * @param a - First value (string or number)
   * @param b - Second value (string or number)
   * @returns -1 if a < b, 0 if a === b, 1 if a > b
   */
  static compare(a: string | number, b: string | number): -1 | 0 | 1 {
    const decimalA = new Decimal(a.toString());
    const decimalB = new Decimal(b.toString());
    return decimalA.comparedTo(decimalB) as -1 | 0 | 1;
  }

  /**
   * Checks if a value is zero.
   *
   * @param value - Value to check (string or number)
   * @returns True if value is zero
   */
  static isZero(value: string | number): boolean {
    return new Decimal(value.toString()).isZero();
  }

  /**
   * Checks if a value is positive.
   *
   * @param value - Value to check (string or number)
   * @returns True if value is greater than zero
   */
  static isPositive(value: string | number): boolean {
    return new Decimal(value.toString()).isPositive() && !new Decimal(value.toString()).isZero();
  }

  /**
   * Checks if a value is negative.
   *
   * @param value - Value to check (string or number)
   * @returns True if value is less than zero
   */
  static isNegative(value: string | number): boolean {
    return new Decimal(value.toString()).isNegative();
  }

  /**
   * Returns the absolute value.
   *
   * @param value - Value (string or number)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Absolute value as string
   */
  static abs(value: string | number, decimals = 2): string {
    return new Decimal(value.toString()).abs().toFixed(decimals);
  }

  /**
   * Calculates percentage of a value.
   *
   * @param value - Base value (string or number)
   * @param percentage - Percentage (e.g., 15 for 15%)
   * @param decimals - Number of decimal places (default: 2)
   * @returns Percentage amount as string
   */
  static percentage(value: string | number, percentage: string | number, decimals = 2): string {
    const result = new Decimal(value.toString())
      .times(new Decimal(percentage.toString()))
      .dividedBy(100);
    return result.toFixed(decimals);
  }
}
